import { ReactNode, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Field, Form, Formik } from 'formik';
import { TextField } from 'formik-mui';
import i18next from "i18next";
import { useTranslation } from "react-i18next";
import { LinearProgress } from '@mui/material';
import { Link, useSearchParams } from 'react-router-dom';
import { object, ref, string } from 'yup';

import { resetPassword } from '../../api';

interface ResetPasswordValues {
  email: string;
  password: string;
  token: string | null;
}

const getCharacterValidationError = (str: string) => {
  return `Your password must have at least 1 ${str} character`;
};

export const passwordSchema = object({
  email: string().email().required(),
  password: string()
    .required(i18next.t('enter-password') as string)
    // check minimum characters
    .min(8, i18next.t('password-requirements') as string)
    // different error messages for different requirements
    .matches(/[0-9]/, getCharacterValidationError("digit"))
    .matches(/[a-z]/, getCharacterValidationError("lowercase"))
    .matches(/[A-Z]/, getCharacterValidationError("uppercase")),
  confirmPassword: string()
    .required("Please re-type your password")
    // use oneOf to match one of the values inside the array.
    // use "ref" to get the value of passwrod.
    .oneOf([ref("password")], i18next.t('passwords-not-match') as string),
});

const ResetPassword = () => {
  const { t } = useTranslation("translation");

  const [queryParams] = useSearchParams();

  const [error, setError] = useState<string | ReactNode | undefined | null>(undefined);
  const [message, setMessage] = useState<string | ReactNode | undefined | null>(undefined);

  const onChangePassword = async (values: ResetPasswordValues) => {
    console.log(values);
    const response = await resetPassword(values.email, values.password, values.token);

    if (!response)
      setError(t("internet-disconnected"));
    else if (response.status !== 200)
      setError(t("invalid-email"));
    else
      setMessage(<>
        <p>{t('password-changed')}</p>
        <CheckCircleOutlineIcon className='success' />
        <Button variant='contained' onClick={() => { window.location.href = '/' }}>OK</Button>
      </>);
  }

  return <div className="auth-component">
    <h1>{t("change-password")}</h1>
    <h2>{t("change-password-desc")}</h2>

    <Formik
      initialValues={{
        email: (queryParams.get('email') ? queryParams.get('email') : '') as string,
        password: '',
        token: queryParams.get('token')
      }}
      validationSchema={passwordSchema}
      // validate={validateEmail}
      onSubmit={async (values, { setSubmitting }) => {
        values.token = queryParams.get('token');

        setError(undefined);
        setSubmitting(true);
        await onChangePassword(values);
        setSubmitting(false);
      }}
    >
      {({ submitForm, isSubmitting }) => (
        <Form>
          <Box margin={1}>
            <Field
              component={TextField}
              name="email"
              type="email"
              label="Email"
              inputProps={{
                disabled: true
              }}
            />
          </Box>

          <Box marginY={2} marginX={1}>
            <Field
              component={TextField}
              name="password"
              type="password"
              label="Password"
            />
          </Box>

          <Box marginY={2} marginX={1}>
            <Field
              component={TextField}
              name="confirmPassword"
              type="password"
              label={t("retype-password")}
            />
          </Box>

          <Box margin={1}>
            <Button
              type='submit'
              className="signin-button"
              variant="contained"
              disabled={isSubmitting}
              onClick={submitForm}>
              {t("change-password")}
            </Button>
          </Box>

          <LinearProgress style={{ marginTop: "1em", opacity: isSubmitting ? 1 : 0 }} />
        </Form>
      )}
    </Formik>

    {message && <div className="overlay-message">{message}</div>}

    {error &&
      <Box marginBottom={3}>
        <p className="error-message">{error}</p>
      </Box>}

    <Box margin={1}>
      <Link to='/' className="link-button">{t("cancel")}</Link>
    </Box>
  </div>;
}

export default ResetPassword;
